import React from 'react';

import Alert from '@mui/material/Alert';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { autoCreatePass } from './autoCreate.js';
import { connectReportWatcher } from './localReportWatcher.js';

export default function AutoCreate({
  directory,
  studies,
  refreshStudies,
  studyDirectory,
  secondaryStudyDirectory,
  onStudiesUpdate,
  studyAllowed,
  settings,
  disabled,
  onActiveChange,
}) {
  const [enabled, setEnabled] = React.useState(false);
  const [hours, setHours] = React.useState(24);
  const [includeStripIndex, setIncludeStripIndex] = React.useState(true);
  const [helperToken, setHelperToken] = React.useState('');
  const [helperPath, setHelperPath] = React.useState('');
  const handleStripIndexChange = (event) => setIncludeStripIndex(event.target.checked);
  const handleHelperTokenChange = (event) => setHelperToken(event.target.value);
  const [status, setStatus] = React.useState('');
  const [connectionStatus, setConnectionStatus] = React.useState('');
  const [messages, setMessages] = React.useState([]);
  const [config, setConfig] = React.useState(null);
  const controller = React.useRef(null);
  const handleAutoChange = async (event) => {
    if (!event.target.checked) {
      controller.current?.abort();
      setEnabled(false);
      return;
    }
    try {
      const permission = await directory.requestPermission({ mode: 'readwrite' });
      if (permission !== 'granted')
        throw new Error('Write access to the PDF folder is required for Auto create.');
      const helper = await connectReportWatcher(
        directory,
        helperToken,
        studyDirectory,
        studies,
        onStudiesUpdate,
        secondaryStudyDirectory
      );
      setHelperPath(
        `${helper.path} | Primary studies: ${helper.studyPath}${helper.secondaryStudyPath ? ` | Secondary studies: ${helper.secondaryStudyPath}` : ''}`
      );
      setConfig({
        directory,
        studies,
        refreshStudies,
        hours,
        helper,
        collectReports: helper.collectReports,
        getStudies: helper.getStudies,
        settings: { ...settings, includeStripIndex },
      });
      setMessages([]);
      setEnabled(true);
    } catch (error) {
      setStatus(error.message);
    }
  };
  React.useEffect(() => {
    if (!enabled || !config) return undefined;
    const aborter = new AbortController();
    controller.current = aborter;
    const seen = new Map();
    onActiveChange(true);
    const reportStatus = (name, message) => {
      if (!aborter.signal.aborted)
        setMessages((current) =>
          [{ name, message }, ...current.filter((item) => item.name !== name)].slice(0, 50)
        );
    };
    const fail = (error) => {
      if (!aborter.signal.aborted) {
        setStatus(error.message);
        setEnabled(false);
        aborter.abort();
      }
    };
    config.helper
      .start(aborter.signal, (message) => {
        if (!aborter.signal.aborted) setConnectionStatus(message);
      })
      .catch(fail);
    const work = async () => {
      try {
        while (!aborter.signal.aborted) {
          const run = () =>
            autoCreatePass({ ...config, signal: aborter.signal, seen, reportStatus });
          if (navigator.locks)
            await navigator.locks.request('hr-report-auto-create', { signal: aborter.signal }, run);
          else await run();
          if (!aborter.signal.aborted) setStatus('Ready — new PDFs are queued automatically.');
          await config.helper.waitForWork(seen, aborter.signal);
        }
      } catch (error) {
        fail(error);
      }
    };
    work();
    return () => {
      aborter.abort();
      onActiveChange(false);
    };
  }, [enabled, config, onActiveChange]);
  return (
    <Stack spacing={1} sx={{ my: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center">
        <FormControlLabel
          label="Auto create"
          control={
            <Checkbox
              checked={enabled}
              onChange={handleAutoChange}
              disabled={!enabled && (disabled || !directory || !studyAllowed)}
            />
          }
        />
        <TextField
          select
          size="small"
          label="Look back (hours)"
          value={hours}
          disabled={enabled}
          onChange={(event) => setHours(Number(event.target.value))}
          sx={{ minWidth: 170 }}
        >
          {[1, 2, 4, 8, 12, 24, 48, 72, 168, 720].map((value) => (
            <MenuItem key={value} value={value}>
              {value} hours
            </MenuItem>
          ))}
        </TextField>
        <FormControlLabel
          label="Include strip index"
          control={
            <Checkbox
              checked={includeStripIndex}
              onChange={handleStripIndexChange}
              disabled={enabled}
            />
          }
        />
      </Stack>
      <TextField
        size="small"
        type="password"
        label="Local folder helper token"
        value={helperToken}
        onChange={handleHelperTokenChange}
        disabled={enabled}
        autoComplete="off"
        helperText="Run tools/hr-folder-watcher/start-watcher.cmd on this PC, then paste its token."
      />
      {helperPath && <Typography variant="body2">Watched report folder: {helperPath}</Typography>}
      <Typography variant="caption">
        Scans reports once using Hours Back, then checks only files reported by the local helper.
        Push notifications replace idle polling. Pending files get two stability checks at least 2
        seconds apart. The same helper watches the study folder and updates changed REP/QRS files.
        Unmatched PDFs retry when their study changes. Existing _HR outputs and other unchanged
        failures are skipped. Keep the helper and this page open.
      </Typography>
      {enabled && connectionStatus && <Alert severity="info">{connectionStatus}</Alert>}
      {status && <Alert severity="info">{enabled ? status : 'Auto create off. ' + status}</Alert>}
      {messages.map((item) => (
        <Typography key={item.name} variant="body2">
          {item.name}: {item.message}
        </Typography>
      ))}
    </Stack>
  );
}
